// QEW tags, drawn on the iPad. A port of safetag_qew_tags.py v2.0 (layout locked v1.1, S86, option C1).
// Add Site saves the PDF into 03 QEW Bag Tags (ADD_SITE_WORDS.md, approved 2026-10-23).
//
// Same geometry as the QR labels (qr-label-pdf.ts): the script's 713 × 285 px drawing, placed on a
// 2.25 × 1.0 in page at (0, 0), 2.25 × 0.90 in, moved by the QR roll offset QR labels use (shared, 9:10 AM). Drawn here as vectors in that pixel space.
//   left  : QR for https://getsafetag.com/?tag=<TAG_ID>
//   right : Tag ID / rule / QUALIFIED ELECTRICAL, WORKER BAG TAG / rule / two footer lines
// Fonts: the script uses Arial; the PDF standard Helvetica has the same widths.
// Toro Max: GP 1 x 3, Scale Default. The 2.25 × 1.0 in tag sits centered on a 3 × 1 in page, like the QR labels.
// One tag per page.

import { PDFDocument, rgb, StandardFonts, type PDFFont, type PDFPage } from "pdf-lib";
import QRCode from "qrcode";
import { LABEL_H, LABEL_W, QR_PAGE } from "./qr-label-pdf";
import { leadPt } from "./driver-page";
import { shiftPage, START_OFFSET, type PrinterOffset } from "./printer-offset";

const MARGIN = 13;
const QR_SIZE = 250;
const PAGE_W = 2.25 * 72;
const PAGE_H = 1.0 * 72;
const BUFFER_TOP = 0.1 * 72;
const LEAD = leadPt(2.25, QR_PAGE);

export const TAG_URL = (tagId: string) => `https://getsafetag.com/?tag=${tagId}`;
export const MSG_LINES = ["QUALIFIED ELECTRICAL", "WORKER BAG TAG"] as const;
export const FOOT_LINES = ["Alltech | Connecting People, PPE,", "and Safe Work Practices"] as const;

/** PIL's ascender drop and cap height, as fractions of the font size (same as the QR labels). */
const ASCENT = 0.92;
const CAP = 0.72;

const black = rgb(0, 0, 0);

/** The script's fit_font: shrink one px at a time until it fits, down to 8. */
export function fitSize(text: string, start: number, maxW: number, width: (t: string, s: number) => number): number {
  let size = start;
  while (size > 8 && width(text, size) > maxW) size--;
  return size;
}

function qrModules(tagId: string): boolean[][] {
  const q = QRCode.create(TAG_URL(tagId), { errorCorrectionLevel: "H" });
  const n = q.modules.size;
  const rows: boolean[][] = [];
  for (let y = 0; y < n; y++) {
    const row: boolean[] = [];
    for (let x = 0; x < n; x++) row.push(Boolean(q.modules.get(x, y)));
    rows.push(row);
  }
  return rows;
}

interface Fonts {
  bold: PDFFont;
  plain: PDFFont;
}

function drawTag(page: PDFPage, tagId: string, f: Fonts): void {
  const sx = PAGE_W / LABEL_W;
  const sy = (PAGE_H - BUFFER_TOP) / LABEL_H;
  const X = (px: number) => LEAD + px * sx;
  const Y = (py: number) => (LABEL_H - py) * sy;
  const rect = (x: number, y: number, w: number, h: number) => page.drawRectangle({ x: X(x), y: Y(y + h), width: w * sx, height: h * sy, color: black });
  const hline = (x1: number, x2: number, y: number, w: number) => rect(x1, y - w / 2, x2 - x1, w);
  const vline = (x: number, y1: number, y2: number, w: number) => rect(x - w / 2, y1, w, y2 - y1);
  const text = (s: string, font: PDFFont, size: number, rx: number, rw: number, top: number) => {
    const w = font.widthOfTextAtSize(s, size);
    page.drawText(s, { x: X(rx + (rw - w) / 2), y: Y(top + ASCENT * size), size: size * sy, font, color: black });
  };

  // Border, 4 px.
  rect(0, 0, LABEL_W, 4);
  rect(0, LABEL_H - 4, LABEL_W, 4);
  rect(0, 0, 4, LABEL_H);
  rect(LABEL_W - 4, 0, 4, LABEL_H);

  // QR, border 1 module, 250 px, centered top to bottom.
  const mods = qrModules(tagId);
  const cell = QR_SIZE / (mods.length + 2);
  const qy = Math.floor((LABEL_H - QR_SIZE) / 2);
  for (let y = 0; y < mods.length; y++) {
    let x = 0;
    while (x < mods.length) {
      if (!mods[y]![x]) {
        x++;
        continue;
      }
      const start = x;
      while (x < mods.length && mods[y]![x]) x++;
      rect(MARGIN + (start + 1) * cell, qy + (y + 1) * cell, (x - start) * cell, cell);
    }
  }

  const divX = MARGIN + QR_SIZE + MARGIN;
  vline(divX, MARGIN, LABEL_H - MARGIN, 2);
  const rx = divX + 10;
  const rw = LABEL_W - rx - MARGIN;
  const wb = (t: string, s: number) => f.bold.widthOfTextAtSize(t, s);
  const wp = (t: string, s: number) => f.plain.widthOfTextAtSize(t, s);

  // Tag ID.
  const tagSize = fitSize(tagId, 30, rw, wb);
  const topY = MARGIN + 14;
  text(tagId, f.bold, tagSize, rx, rw, topY);
  const line1 = topY + Math.round(CAP * tagSize) + 20;
  hline(rx, LABEL_W - MARGIN, line1, 1);

  // Lower rule: room for two footer lines.
  const footSize = fitSize(FOOT_LINES[0], 20, rw, wp);
  const fh = Math.round(0.9 * footSize);
  const line2 = LABEL_H - MARGIN - (fh * 2 + 6 + 18);

  // Message, centered between the rules.
  const msgSize = fitSize(MSG_LINES[0], 30, rw, wb);
  const mh = Math.round(CAP * msgSize);
  let y = line1 + Math.floor((line2 - line1 - (mh * 2 + 10)) / 2);
  for (const t of MSG_LINES) {
    text(t, f.bold, msgSize, rx, rw, y);
    y += mh + 10;
  }
  hline(rx, LABEL_W - MARGIN, line2, 1);

  // Footer.
  y = line2 + Math.floor((LABEL_H - MARGIN - line2 - (fh * 2 + 6)) / 2);
  for (const t of FOOT_LINES) {
    text(t, f.plain, footSize, rx, rw, y);
    y += fh + 6;
  }
}

/** One page per tag (the 2.25 × 1.0 in tag centered on GP 1 x 3), in the order given. */
export async function qewTagsPdf(tagIds: readonly string[], offset: PrinterOffset = START_OFFSET.qrRoll): Promise<Uint8Array> {
  const pdf = await PDFDocument.create();
  pdf.setTitle("SafeTag QEW Bag Tags");
  pdf.setCreator("SafeTag Add Site");
  const fonts: Fonts = { bold: await pdf.embedFont(StandardFonts.HelveticaBold), plain: await pdf.embedFont(StandardFonts.Helvetica) };
  for (const id of tagIds) {
    const page = pdf.addPage([QR_PAGE.length * 72, PAGE_H]);
    drawTag(page, id, fonts);
    shiftPage(page, offset);
  }
  return pdf.save();
}
